// src/pages/Promo.js

import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';

import PromoImage from '../assets/Promo.png';
import TopBar from '../components/TopBar/TopBar';
import Background from '../components/Background/Background';

const Promo = () => {
  // === TEXT BOX POSITIONING CONTROLS ===
  const boxPositionX = '79.5%';
  const boxPositionY = '55%';
  const boxWidth = '30%';

  // === DRAG & DROP STATE & LOGIC ===
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  // Unified handler for both mouse and touch start
  const handleDragStart = (e) => {
    setIsDragging(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    dragStart.current = {
      x: clientX - offset.x,
      y: clientY - offset.y,
    };
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const handleDragMove = (e) => {
      if (!isDragging) return;

      // Prevent default scrolling behavior on touch devices while dragging
      if (e.touches && e.cancelable) {
        e.preventDefault();
      }

      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;

      setOffset({
        x: clientX - dragStart.current.x,
        y: clientY - dragStart.current.y,
      });
    };

    const handleDragEnd = () => {
      setIsDragging(false);
      document.body.style.userSelect = '';
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      // passive: false is required to allow preventDefault() on touchmove
      document.addEventListener('touchmove', handleDragMove, { passive: false });
      document.addEventListener('touchend', handleDragEnd);
    } else {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleDragMove);
      document.removeEventListener('touchend', handleDragEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleDragMove);
      document.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging]);

  return (
    <Background>
      {/* === INJECT TOP MENU BAR === */}
      <TopBar activeButton='promo' />

      {/* === INVISIBLE IMAGE CONTAINER === */}
      <Box
        sx={{
          position: 'absolute',
          top: '70px',
          left: 0,
          right: 0,
          // Removed bottom constraint so the height can flow naturally
          zIndex: 1,
        }}>
        <img
          src={PromoImage}
          alt='loKtr Promo'
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
        />
      </Box>

      {/* === CENTRAL DRAGGABLE TEXT BOX === */}
      <Box
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        sx={{
          position: 'absolute',
          left: boxPositionX,
          top: boxPositionY,
          transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
          width: boxWidth,
          minWidth: '350px',
          height: 'auto',
          backgroundColor: '#ffffff',
          border: '1px solid rgb(237, 109, 29)',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: isDragging
            ? '0px 12px 24px rgba(0, 0, 0, 0.3)'
            : '0px 4px 12px rgba(0, 0, 0, 0.1)',
          zIndex: isDragging ? 100 : 20,
          cursor: isDragging ? 'grabbing' : 'grab',
          transition: isDragging ? 'none' : 'box-shadow 0.2s ease',
          touchAction: 'none',
        }}>
        <Typography
          sx={{
            fontFamily: '"Poppins", sans-serif',
            fontSize: '12pt',
            fontWeight: 600,
            textAlign: 'left',
            color: '#000000',
            lineHeight: 1.2,
            mb: 1,
          }}>
          Promo: Elevate Your Marketing
        </Typography>

        <Typography
          sx={{
            fontFamily: '"Poppins", sans-serif',
            fontSize: '12pt',
            textAlign: 'left',
            color: '#000000',
            lineHeight: 1.2,
            mb: 2,
          }}>
          Leverage your proven track record to win new business. The Promo tools help you
          showcase your best work effortlessly:
        </Typography>

        {/* BULLET POINTS */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[
            'Targeted Pitches: Generate custom portfolios and map views for specific clients or industries.',
            'Automated Updates: Keep prospects engaged with automated newsfeeds detailing your latest project completions.',
            'Fully Contained Email Broadcast Platform: Keep prospects engaged with automated newsfeeds detailing your latest project completions.',
            'Stand Out: Provide a memorable, visually impressive digital experience that sets you apart from competitors.',
          ].map((text, index) => (
            <Typography
              key={index}
              sx={{
                fontFamily: '"Poppins", sans-serif',
                fontSize: '12pt',
                textAlign: 'left',
                color: '#000000',
                display: 'flex',
                alignItems: 'flex-start',
                lineHeight: 1.2,
              }}>
              <span style={{ marginRight: '12px' }}>•</span>
              <span>{text}</span>
            </Typography>
          ))}
        </Box>
      </Box>
    </Background>
  );
};

export default Promo;
